import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { RequestFieldsEnum } from '@/enums/RequestFieldsEnum'
import { SiteContextBasedIdEnum } from '@/enums/SiteContextBasedIdEnum'
import { useAsmSession } from '@/hooks/useAsmSession'
import { useBaseRequest } from '@/hooks/useBaseRequest'
import { useOrgScopedQueryCacheKey } from '@/hooks/useOrgScopedQueryCacheKey'
import { useSiteContext } from '@/hooks/useSiteContext'
import { b2bUsersClient } from '@/lib/clients/b2bUsersClient'
import { usersClient } from '@/lib/clients/usersClient'

import type { GetOrgUserRequest, GetUserRequest } from '@/gen/ecom/apis'

interface UseUserParams {
  id?: string
}

export function useUser(params?: UseUserParams) {
  const { isAuthorized } = useAsmSession()
  const { userBaseRequest } = useBaseRequest()
  const { getBaseSiteId } = useSiteContext()
  const overrides: RequestInit = {
    headers: { 'Content-Type': 'application/json' },
  }

  const siteContext = getBaseSiteId()
  const requestedUserId = params?.id?.trim() ? params.id : userBaseRequest.userId

  const getUserRequest: GetUserRequest = {
    ...userBaseRequest,
    userId: requestedUserId,
    fields: RequestFieldsEnum.Full,
  }

  const getOrgUserRequest: GetOrgUserRequest = {
    ...userBaseRequest,
    userId: requestedUserId,
    fields: RequestFieldsEnum.Full,
  }

  // Org-scoped: an unscoped key showed the previous organisation's profile after a switch.
  const getUserCacheKey = useOrgScopedQueryCacheKey(usersClient.getUser.name, getUserRequest)
  const getOrgUserCacheKey = useOrgScopedQueryCacheKey(b2bUsersClient.getOrgUser.name, getOrgUserRequest)

  // keepPreviousData stops re-keying from briefly emptying `orgUnits`, which would unmount <CustomerSelector>.
  const b2cOrgUserQuery = useQuery({
    queryKey: getUserCacheKey,
    queryFn: () => usersClient.getUser(getUserRequest, overrides),
    enabled: siteContext === SiteContextBasedIdEnum['dk-b2c'] && isAuthorized,
    placeholderData: keepPreviousData,
  })

  const b2bOrgUserQuery = useQuery({
    queryKey: getOrgUserCacheKey,
    queryFn: () => b2bUsersClient.getOrgUser(getOrgUserRequest, overrides),
    enabled: siteContext.includes('b2b') && isAuthorized,
    placeholderData: keepPreviousData,
  })

  return siteContext.includes('b2b') ? b2bOrgUserQuery : b2cOrgUserQuery
}
